/**
 * Typed API call wrappers organized by domain.
 * All methods return the unwrapped data (not the Axios response wrapper).
 */

import { apiClient } from './apiClient';
import type { ApiResponse, PaginatedResponse, ListParams } from '@/types/api.types';
import type {
  Patient,
  PatientSummary,
  PatientListParams,
  Plan,
} from '@/types/patient.types';
import type {
  AuthResponse,
  LoginCredentials,
  PendingDisclaimer,
  StaffUser,
  AccountAddons,
} from '@/types/auth.types';

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const authApi = {
  login: (credentials: LoginCredentials) =>
    apiClient
      .post<ApiResponse<AuthResponse>>('/auth/staff/login', credentials)
      .then((r) => r.data.data),

  exchangeCorporateCode: (code: string) =>
    apiClient
      .post<ApiResponse<AuthResponse>>('/auth/corp/exchange', { code })
      .then((r) => r.data.data),

  // Note the shape difference from login/exchange: `me` returns the user's
  // fields flattened at the top level, with `modules`, `pendingDisclaimers` and
  // `ownerAgreementHold` alongside them rather than nested under `user`.
  me: () =>
    apiClient
      .get<ApiResponse<StaffUser & {
        modules?: import('@/services/accountModules.service').AccountModule[];
        pendingDisclaimers?: PendingDisclaimer[];
        ownerAgreementHold?: boolean;
      }>>('/auth/me')
      .then((r) => r.data.data),

  // Staff password reset. Backend endpoints `/auth/staff/forgot-password` and
  // `/auth/staff/reset-password` are pending — see notes in the AuthController.
  forgotPassword: (loginName: string, userName: string) =>
    apiClient
      .post<ApiResponse<{ sent: boolean }>>('/auth/staff/forgot-password', { loginName, userName })
      .then((r) => r.data.data),

  resetPassword: (token: string, password: string) =>
    apiClient
      .post<ApiResponse<{ ok: boolean }>>('/auth/staff/reset-password', { token, password })
      .then((r) => r.data.data),
};

// ─── Disclaimers (first-login gate) ──────────────────────────────────────────

export const disclaimersApi = {
  getPending: () =>
    apiClient
      .get<ApiResponse<PendingDisclaimer[]>>('/disclaimers/pending')
      .then((r) => r.data.data ?? []),

  accept: (
    slug: string,
    code: string,
    version: number,
    signedName: string,
    signedBirthDate: string,
  ) =>
    apiClient
      .post<ApiResponse<{ slug: string; code: string; version: number }>>(
        '/disclaimers/accept',
        {
          slug,
          code,
          version,
          signedName,
          signedBirthDate,
        },
      )
      .then((r) => r.data.data),
};

// ─── Patients ─────────────────────────────────────────────────────────────────

export const patientsApi = {
  list: (params: PatientListParams) =>
    apiClient
      .get<PaginatedResponse<PatientSummary>>('/patients', { params })
      .then((r) => r.data),

  search: (query: string) =>
    apiClient
      .get<ApiResponse<PatientSummary[]>>('/patients/search', { params: { q: query } })
      .then((r) => r.data.data),

  getById: (id: number) =>
    apiClient.get<ApiResponse<Patient>>(`/patients/${id}`).then((r) => r.data.data),

  create: (data: Partial<Patient>) =>
    apiClient.post<ApiResponse<Patient>>('/patients', data).then((r) => r.data.data),

  update: (id: number, data: Partial<Patient>) =>
    apiClient.put<ApiResponse<Patient>>(`/patients/${id}`, data).then((r) => r.data.data),

  softDelete: (id: number) =>
    apiClient.delete(`/patients/${id}`).then(() => undefined),

  getPlans: (patientId: number) =>
    apiClient
      .get<ApiResponse<Plan[]>>(`/patients/${patientId}/plans`)
      .then((r) => r.data.data),
};

// ─── Plans ────────────────────────────────────────────────────────────────────

export const plansApi = {
  getById: (planId: number) =>
    apiClient.get<ApiResponse<Plan>>(`/plans/${planId}`).then((r) => r.data.data),

  create: (data: Partial<Plan>) =>
    apiClient.post<ApiResponse<Plan>>('/plans', data).then((r) => r.data.data),

  update: (planId: number, data: Partial<Plan>) =>
    apiClient.put<ApiResponse<Plan>>(`/plans/${planId}`, data).then((r) => r.data.data),
};

// ─── Admin ────────────────────────────────────────────────────────────────────

export const adminApi = {
  getAccountAddons: () =>
    apiClient.get<ApiResponse<AccountAddons>>('/admin/addons').then((r) => r.data.data),

  getUsers: (params?: ListParams) =>
    apiClient
      .get<PaginatedResponse<StaffUser>>('/admin/users', { params })
      .then((r) => r.data),

  createUser: (data: Partial<StaffUser>) =>
    apiClient.post<ApiResponse<StaffUser>>('/admin/users', data).then((r) => r.data.data),

  updateUser: (id: number, data: Partial<StaffUser>) =>
    apiClient.put<ApiResponse<StaffUser>>(`/admin/users/${id}`, data).then((r) => r.data.data),

  deleteUser: (id: number) =>
    apiClient.delete(`/admin/users/${id}`).then(() => undefined),
};
